import React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { useSelector, useDispatch } from 'react-redux'
import GridItem from './_GridItem'
import ResizeHandle from './_ResizeHandle'
import { GridItemPosition, GridItemSize } from 'Vendor/Utils/StudioTypes'
import { AppDispatch } from 'Vendor/Store/store'
import _GridItemMenu from './_GridItemMenu'
import {
  itemDeleted,
  itemDuplicated,
  itemRotated,
  itemSelected,
} from 'Vendor/Store/studio'
import {
  selectSelectedItemIds,
  selectActivePageId,
  selectCoverImageId,
} from 'Vendor/Store/studio/selectors'
import { clearActiveTool, selectActiveTool } from 'Vendor/Store/studio/activeToolSlice'

export interface DisplayGridItemData {
  id: string
  itemId?: number
  assetId: string
  type: 'TEXT' | 'PRODUCT' | 'FILE' | 'COLOR' | 'EXTERNAL_IMAGE'
  content: { [key: string]: any }
  position: GridItemPosition
  size: GridItemSize
  styles: React.CSSProperties
  zIndex: number
}
interface DraggableGridItemProps {
  item: any
}

const DraggableGridItem: React.FC<DraggableGridItemProps> = ({ item }) => {
  const dispatch = useDispatch<AppDispatch>()
  const activeTool = useSelector(selectActiveTool);

  const selectedItemIds = useSelector(selectSelectedItemIds)
  const activePageId = useSelector(selectActivePageId)
  const coverImageId = useSelector(selectCoverImageId)

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: item.id,
      data: { type: 'move', item },
    })

  const isSelected = selectedItemIds.includes(item.id)
  const isCover = item.type === 'FILE' && item.itemId === coverImageId

  const style = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : { }

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!activePageId) {
      return;
    }

    if (activeTool.itemId !== item.id) {
        dispatch(clearActiveTool());
    }

    dispatch(itemSelected({
      pageId: activePageId,
      itemId: item.id,
      metaKey: e.metaKey || e.ctrlKey
    }));
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.resize-handle')) {
      e.stopPropagation()
      return
    }

    if (listeners?.onPointerDown) {
      listeners.onPointerDown(e as any)
    }
  }

  const handleDuplicate = () => {
    if (!activePageId) return

    dispatch(itemDuplicated({ pageId: activePageId, itemId: item.id }))
  }

  const handleDelete = () => {
    if (!activePageId) return

    dispatch(itemDeleted({ pageId: activePageId }));
  }

  const handleRotate = () => {
    if (!activePageId) return

    dispatch(itemRotated({ pageId: activePageId, itemId: item.id }))
  }

  const pos = item.position
  const size = item.size

  const getClassNames = () => {
    let classes = 'position-relative draggable-grid-item'
    if (isSelected) {
      classes += ' selected-item'
    }
    if (isDragging) {
      classes += ' invisible'
    }
    return classes
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        gridColumnStart: pos.col_start,
        gridColumnEnd: `span ${size.col_span}`,
        gridRowStart: pos.row_start,
        gridRowEnd: `span ${size.row_span}`,
        ...style,
      }}
      className={getClassNames()}
      {...attributes}
      onPointerDown={handlePointerDown}
      onClick={handleSelect}
      data-id={item.id}  
    >
      <GridItem item={item} isCover={isCover} isSelected={isSelected} />

      {isSelected && selectedItemIds.length === 1 && (
        <>
          <div onClick={(e) => e.stopPropagation()}>
            <_GridItemMenu
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              onRotate={handleRotate}
            />
          </div>
          <ResizeHandle item={item} handle="top-left" />
          <ResizeHandle item={item} handle="top-center" />
          <ResizeHandle item={item} handle="top-right" />
          <ResizeHandle item={item} handle="middle-left" />
          <ResizeHandle item={item} handle="middle-right" />
          <ResizeHandle item={item} handle="bottom-left" />
          <ResizeHandle item={item} handle="bottom-center" />
          <ResizeHandle item={item} handle="bottom-right" />
        </>
      )}
    </div>
  )
}

export default DraggableGridItem
